import { openOlark } from "@utils/olark";
import React, { Component, Fragment } from "react";
import styled from "styled-components";

import { BettoAlertItem } from "./BettoAlertItem.jsx";
import { BETTO_ALERT_ACTIONS } from "./bettoAlertVariants";

const BettoStaticWarningBar = styled.div`
	position: fixed;
	left: 50%;
	bottom: 12px;
	transform: translateX(-50%);
	z-index: 10000;
	max-width: calc(100vw - 24px);
	width: 700px;
	padding: 12px 18px;
	border-radius: 12px;
	background: var(--bt-auth-modal-bg, #171c2a);
	color: var(--bt-auth-heading-color, #e5f0fe);
	border: 1px solid var(--bt-auth-modal-border, #212737);
	text-align: center;
	font-size: 14px;
	line-height: 1.4;

	@media (max-width: 768px) {
		bottom: 8px;
		font-size: 13px;
		padding: 10px 14px;
	}
`;

const getAlertKey = (alertData, index) =>
	alertData?.expiry ? `${alertData.expiry}-${index}` : `${alertData?.msg || "alert"}-${index}`;

export class BettoAlert extends Component {
	constructor(props) {
		super(props);
		this.state = {
			profileUpdateTimer: null,
			pendingAuthType: null,
		};
		this.interval = 3000;
		this.close = this.close.bind(this);
		this.handleButtonAction = this.handleButtonAction.bind(this);
		this.alertExpiryTimer = setInterval(this.updateAlerts.bind(this), 1000);
	}

	updateAlerts() {
		const alerts = this.props.alert?.alerts;

		if (!alerts?.length) {
			return;
		}

		const now = new Date();

		for (let index = alerts.length - 1; index >= 0; index -= 1) {
			const alert = alerts[index];

			if (!alert.persistent && now - alert.expiry > this.interval) {
				this.props.hideAlert(index);
			}
		}
	}

	runAlertComplete(alertData) {
		if (typeof alertData?.completeCallback === "function") {
			alertData.completeCallback();
			return true;
		}

		return false;
	}

	close(index, { skipComplete = false } = {}) {
		if (this.state.profileUpdateTimer) {
			clearTimeout(this.state.profileUpdateTimer);
			this.setState({ profileUpdateTimer: null });
		}

		const alert = this.props.alert?.alerts?.[index];

		if (!skipComplete && alert?.runCompleteOnClose) {
			this.runAlertComplete(alert);
		}

		if (alert?.callback) {
			alert.callback();
		}

		this.props.hideAlert(index);
	}

	onAccept(index, alertData) {
		if (typeof alertData?.acceptCallback === "function") {
			alertData.acceptCallback();
		}
		this.close(index);
	}

	handleContactSupport() {
		void openOlark();
	}

	componentDidUpdate(prevProps) {
		const currentAlerts = this.props.alert?.alerts || [];
		const prevAlerts = prevProps.alert?.alerts || [];

		if (this.state.pendingAuthType && currentAlerts.length < prevAlerts.length) {
			const pendingAuthType = this.state.pendingAuthType;
			this.setState({ pendingAuthType: null });
			this.props.toggleAuthPopup(true, pendingAuthType);
		}

		if (currentAlerts.length > prevAlerts.length) {
			const newAlert = currentAlerts[currentAlerts.length - 1];
			if (
				newAlert.msg === "PROFILE_UPDATE_REQUIRED_MESSAGE" &&
				!this.state.profileUpdateTimer
			) {
				const timer = setTimeout(() => {
					window.location.href = "/profile/settings";
				}, 10000);
				this.setState({ profileUpdateTimer: timer });
			}
		}
	}

	componentWillUnmount() {
		clearInterval(this.alertExpiryTimer);

		if (this.state.profileUpdateTimer) {
			clearTimeout(this.state.profileUpdateTimer);
		}
	}

	openAuthPopup(index, authType) {
		this.setState({ pendingAuthType: authType });
		this.close(index);

		setTimeout(() => {
			if (!this.state.pendingAuthType) {
				return;
			}

			const pendingAuthType = this.state.pendingAuthType;
			this.setState({ pendingAuthType: null });
			this.props.toggleAuthPopup(true, pendingAuthType);
		}, 0);
	}

	handleButtonAction(index, alertData, button) {
		switch (button.action) {
			case BETTO_ALERT_ACTIONS.ACCEPT:
			case "accept":
				this.onAccept(index, alertData);
				return;
			case BETTO_ALERT_ACTIONS.RESET_PASSWORD:
				this.openAuthPopup(index, "forgot-password");
				return;
			case BETTO_ALERT_ACTIONS.LOGIN:
				this.openAuthPopup(index, "login");
				return;
			case BETTO_ALERT_ACTIONS.SIGNUP:
				this.openAuthPopup(index, "signup");
				return;
			case BETTO_ALERT_ACTIONS.CONTACT_SUPPORT:
			case BETTO_ALERT_ACTIONS.CONTACT_BANK:
				this.close(index);
				this.handleContactSupport();
				return;
			case BETTO_ALERT_ACTIONS.DEPOSIT:
				this.close(index);
				window.location.href = "/profile/deposit";
				return;
			case BETTO_ALERT_ACTIONS.WALLET:
				this.close(index);
				window.location.href = "/profile/wallet";
				return;
			case BETTO_ALERT_ACTIONS.HOME:
				if (!this.runAlertComplete(alertData)) {
					window.location.href = "/";
				}
				this.close(index, { skipComplete: true });
				return;
			case BETTO_ALERT_ACTIONS.PROFILE:
				this.close(index);
				window.location.href = "/profile/settings";
				return;
			case BETTO_ALERT_ACTIONS.TRACK_WITHDRAWAL:
				this.close(index);
				window.location.href = "/profile/withdraw";
				return;
			case BETTO_ALERT_ACTIONS.BET_SLIP:
				this.close(index);
				window.location.href = "/sportsbook";
				return;
			case BETTO_ALERT_ACTIONS.PLACE_BET:
				this.close(index);
				window.location.href = "/sportsbook";
				return;
			case BETTO_ALERT_ACTIONS.LOGOUT:
				this.close(index);
				if (typeof alertData?.acceptCallback === "function") {
					alertData.acceptCallback();
				}
				return;
			case BETTO_ALERT_ACTIONS.STAY_LOGGED_IN:
				if (typeof alertData?.stayLoggedInCallback === "function") {
					alertData.stayLoggedInCallback();
				}
				this.close(index);
				return;
			case BETTO_ALERT_ACTIONS.RESEND_CODE:
				if (typeof alertData?.resendCallback === "function") {
					alertData.resendCallback();
				}
				this.close(index);
				return;
			case BETTO_ALERT_ACTIONS.VERIFY_AGAIN:
				if (typeof alertData?.verifyAgainCallback === "function") {
					alertData.verifyAgainCallback();
				} else {
					this.openAuthPopup(index, "login");
					return;
				}
				this.close(index);
				return;
			case BETTO_ALERT_ACTIONS.EDIT_AMOUNT:
				this.close(index);
				return;
			case BETTO_ALERT_ACTIONS.CONTINUE_ANYWAY:
				if (typeof alertData?.continueCallback === "function") {
					alertData.continueCallback();
				}
				this.close(index);
				return;
			case "customAction":
				if (typeof alertData?.actionCallback === "function") {
					alertData.actionCallback();
				}
				this.close(index);
				return;
			case BETTO_ALERT_ACTIONS.RETRY:
				if (typeof alertData?.retryCallback === "function") {
					alertData.retryCallback();
				}
				this.close(index);
				return;
			case BETTO_ALERT_ACTIONS.CLOSE:
			default:
				this.close(index);
		}
	}

	render() {
		const { translate } = this.props;
		const staticWarning = this.props.alert?.staticWarning;
		const alerts = this.props.alert?.alerts || [];

		return (
			<Fragment>
				{this.props.children}

				{staticWarning?.msg ? (
					<BettoStaticWarningBar>
						{translate(staticWarning.msg, staticWarning.msgParams || {}, {
							renderInnerHtml: true,
						})}
					</BettoStaticWarningBar>
				) : null}

				{alerts.map((alertData, index) => (
					<BettoAlertItem
						key={getAlertKey(alertData, index)}
						alertData={alertData}
						index={index}
						translate={translate}
						onClose={this.close}
						onButtonAction={this.handleButtonAction}
					/>
				))}
			</Fragment>
		);
	}
}
